import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { combineClasses } from "@/lib/utils";
import styles from "./RowActionsMenu.module.scss";

export interface RowAction {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  tone?: "default" | "danger";
  // Renders a separator above this item, for grouping (e.g. destructive
  // actions apart from the rest) — see pages/Waitlist/Waitlist.tsx.
  dividerBefore?: boolean;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
  ariaLabel?: string | undefined;
  triggerClassName?: string | undefined;
}

// A per-row "⋯" menu for a table's trailing actions column. Purely a menu
// shell — what each action actually does is the caller's job.
//
// The menu itself renders through a portal into `document.body`, positioned
// `fixed` from the trigger button's own bounding rect, rather than as an
// absolutely-positioned descendant of the trigger. A table row is exactly
// the kind of ancestor this needs to escape: a scrollable table body clips
// anything beyond its box, and — more subtly — any row that becomes its
// own stacking context (an explicit z-index, a transform, ...) traps a
// menu rendered inside it below every later row, no matter how high the
// menu's own z-index is. A portal sidesteps both failure modes entirely,
// instead of relying on every table this renders inside to avoid both.
const RowActionsMenu = ({
  actions,
  ariaLabel = "Row actions",
  triggerClassName,
}: RowActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const hasFlippedRef = useRef(false);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    hasFlippedRef.current = false;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };

    updatePosition();

    // A scroll (of the table body, or the page) or a resize invalidates the
    // trigger's captured position — closing is simpler and safer than
    // continuously repositioning a menu whose trigger may have scrolled
    // out of view entirely.
    const closeOnScrollOrResize = () => setIsOpen(false);
    window.addEventListener("scroll", closeOnScrollOrResize, true);
    window.addEventListener("resize", closeOnScrollOrResize);

    return () => {
      window.removeEventListener("scroll", closeOnScrollOrResize, true);
      window.removeEventListener("resize", closeOnScrollOrResize);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !menuPosition || hasFlippedRef.current) return;
    const menuRect = menuRef.current?.getBoundingClientRect();
    const triggerRect = triggerRef.current?.getBoundingClientRect();
    if (!menuRect || !triggerRect) return;

    if (menuRect.bottom > window.innerHeight - 8) {
      hasFlippedRef.current = true;
      setMenuPosition((prev) =>
        prev ? { ...prev, top: Math.max(triggerRect.top - menuRect.height - 4, 4) } : prev,
      );
    }
  }, [isOpen, menuPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        ref={triggerRef}
        className={combineClasses(styles.trigger, triggerClassName)}
        aria-label={ariaLabel}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        &#8943;
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            style={{ position: "fixed", top: menuPosition.top, right: menuPosition.right }}
          >
            {actions.map((action) => (
              <button
                key={action.key}
                type="button"
                className={combineClasses(
                  styles.menuItem,
                  action.dividerBefore && styles.dividerBefore,
                  action.tone === "danger" && styles.danger,
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsOpen(false);
                  action.onClick();
                }}
              >
                <span className={styles.menuIcon}>{action.icon}</span>
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default RowActionsMenu;
